import { useEffect, useState } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Btn } from "../../../AbstractElements";
import useCustomToasts from "../../../hooks/useCustomToast";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import { Setting, WithdrawalSettingTitle } from "../../../Utils/Constants";

interface FormValues {
  id?: number;
  is_target: string;
  target_limit?: number | null;
}

export default function WithdrawalSetting() {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      is_target: "",
      target_limit: null,
    },
  });
  const [loading, setLoading] = useState(true);

  const isTargetValue = watch("is_target");
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [id, setId] = useState(false);
  // Fetch existing setting
  useEffect(() => {
    const fetchSetting = async () => {
      setLoading(true);

      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${baseUrl}withdrawal_setting/getall`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = response.data;
        if (data && Object.keys(data).length > 0 && "is_target" in data) {
          const formattedTargetLimit =
            data.target_limit !== null && Number(data.target_limit) % 1 === 0
              ? parseInt(data.target_limit)
              : data.target_limit;

          setValue("id", data.id);
          setValue("is_target", data.is_target ? "yes" : "no");
          setValue("target_limit", formattedTargetLimit);
          setIsEditMode(true);
        } else {
          setIsEditMode(false);
        }
      } catch (error) {
        console.error("Error fetching withdrawal setting:", error);
        ErrorToast("Failed to fetch setting");
      } finally {
        setLoading(false);
      }
    };

    fetchSetting();
  }, [setValue]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const token = localStorage.getItem("token");

      const payload = {
        id: data.id,
        is_target: data.is_target === "yes",
        target_limit: data.is_target === "yes" ? data.target_limit : null,
      };

      const endpoint = isEditMode
        ? `${baseUrl}withdrawal_setting/update`
        : `${baseUrl}withdrawal_setting/store`;

      const response = await axios.post(endpoint, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      // console.log(response.data);
      if (response.status === 200 || response.status === 201) {
        const message = isEditMode
          ? "Data updated successfully"
          : "Data created successfully";
        if (response.data?.data?.id) {
          
          setValue("id", response.data.data.id);
        }
        if (!isEditMode) {
          // 🔥 Set edit mode true after creation
          setIsEditMode(true);
        }
        SuccessToast(message, "/withdrawal_setting");
      } else {
        ErrorToast("Operation failed");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as any)?.message ||
        "An unexpected error occurred";
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };
  return (
    <>
      <Breadcrumbs
        pageTitle={WithdrawalSettingTitle}
        parent={Setting}
        title={WithdrawalSettingTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <div className="card-wrapper border rounded-3 p-3">
                    <h6 className="sub-title fw-bold mb-2">
                      Activate Withdrawal Setting?
                    </h6>
                    <Controller
                      name="is_target"
                      control={control}
                      rules={{ required: "Please select an option" }}
                      render={({ field }) => (
                        <div className="d-flex gap-4">
                          {["yes", "no"].map((option) => (
                            <FormGroup check key={option}>
                              <Input
                                type="radio"
                                value={option}
                                checked={field.value === option}
                                onChange={field.onChange}
                                id={`radio-${option}`}
                                name="is_target"
                              />
                              <Label
                                check
                                htmlFor={`radio-${option}`}
                                className="ms-1 text-capitalize"
                              >
                                {option}
                              </Label>
                            </FormGroup>
                          ))}
                        </div>
                      )}
                    />
                    {errors.is_target && (
                      <div className="text-danger mt-1">
                        {errors.is_target.message}
                      </div>
                    )}
                  </div>
                </Col>

                {isTargetValue === "yes" && (
                  <Col xs={12}>
                    <Label htmlFor="target_limit">
                      Target Amount {currency ? `(${currency})` : ""}
                    </Label>
                    <Controller
                      name="target_limit"
                      control={control}
                      rules={{
                        required: "Target amount is required",
                        min: { value: 0, message: "Amount must be at least 0" },
                      }}
                      render={({ field }) => (
                        <>
                          <Input
                            type="number"
                            id="target_limit"
                            placeholder="Enter target amount"
                            {...field}
                            value={field.value ?? ""}
                            invalid={!!errors.target_limit}
                            onKeyDown={(e) => {
                              handleKeyDown(e);
                              handleAmountKeyDown(e);
                            }}
                            onWheel={(e) =>
                              (e.target as HTMLInputElement).blur()
                            }
                          />
                          {errors.target_limit && (
                            <FormFeedback>
                              {errors.target_limit.message}
                            </FormFeedback>
                          )}
                        </>
                      )}
                    />
                  </Col>
                )}

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {isEditMode ? "Update" : "Submit"}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer toastStyle={{ userSelect: "none" }} />
    </>
  );
}
